/**
 * Labels + formatters for the Insights → Ticket follow-through tab.
 *
 * Platform-neutral. The server does every count, percentage, grouping and
 * sort; this file only names the options and ticket types and formats the
 * figures.
 */

import type {
  TicketFollowthroughGroup,
  TicketFollowthroughRoles,
  TicketFollowthroughWindow,
} from "../data/api-types";

export const TICKET_FOLLOWTHROUGH_WINDOW_OPTIONS: TicketFollowthroughWindow[] =
  ["last_30_days", "last_90_days", "all_time"];

export const TICKET_FOLLOWTHROUGH_WINDOW_LABELS: Record<
  TicketFollowthroughWindow,
  string
> = {
  last_30_days: "Raised in the last 30 days",
  last_90_days: "Raised in the last 90 days",
  all_time: "All tickets",
};

export const TICKET_FOLLOWTHROUGH_ROLE_OPTIONS: TicketFollowthroughRoles[] = [
  "sales_team",
  "sales_rep",
  "manager",
  "csr",
  "everyone",
];

export const TICKET_FOLLOWTHROUGH_ROLE_LABELS: Record<
  TicketFollowthroughRoles,
  string
> = {
  sales_team: "Sales reps and managers",
  sales_rep: "Sales reps",
  manager: "Managers",
  csr: "CSRs",
  everyone: "Everyone",
};

export const TICKET_GROUP_LABELS: Record<
  Exclude<TicketFollowthroughGroup, "all">,
  string
> = {
  marketing: "Marketing",
  customer_alerts: "Customer alerts",
  other: "Jobs and other",
};

const TICKET_TYPE_LABELS: Record<string, string> = {
  campaign_contact: "Campaign follow-up",
  campaign_engaged: "Engagement follow-up",
  campaign: "Campaign call",
  campaign_bounce: "Bounced email",
  campaign_engagement_roundup: "Engagement round-up (old)",
  landing_page_lead: "Landing-page lead",
  marketing_trigger: "Triggered email list",
  account_health: "Account health",
  critical_alert: "Critical alert",
  alert: "Alert",
  opportunity: "Opportunity",
  job: "Job",
};

/** A ticket type's display name; a ticket with no subject is a manual one. */
export function ticketTypeLabel(
  subjectType: string | null | undefined,
): string {
  if (!subjectType) return "Manual";
  return TICKET_TYPE_LABELS[subjectType] ?? subjectType.replace(/_/g, " ");
}

/** "12%" (one decimal under 10%, so small shares don't all read as 0%), or "—". */
export function formatFollowthroughPct(pct: number | null | undefined): string {
  if (pct === null || pct === undefined) return "—";
  if (pct > 0 && pct < 10) return `${pct.toFixed(1)}%`;
  return `${Math.round(pct)}%`;
}

/** "5 h" under two days, else "3 days"; "—" when nothing was actioned. */
export function formatHoursToFirstAction(
  hours: number | null | undefined,
): string {
  if (hours === null || hours === undefined) return "—";
  if (hours < 48) return `${Math.max(1, Math.round(hours))} h`;
  return `${Math.round(hours / 24)} days`;
}
